import {Component, OnInit, Input, ViewChild } from '@angular/core';
import {DialogRef, DialogService} from '@progress/kendo-angular-dialog';
import { FormGroup, FormBuilder } from '@angular/forms';
import { PageSubheaderBarConfig } from '../../../fw/pages/page-config-api';
import { FormService } from '../../../fw/services/form.service';
import { SharedService } from '../../shared/shared.service';
import { AutoUnsubscribe } from '../../../fw/decoractors/AutoUnsubscribe';
import {SettingsCashregService} from "./settings-cashreg.service";
import {DynamicGridComponent} from "../../../fw/dynamic-grids/dynamic-grid.component";
import {ColumnDefinition} from "../../../fw/dynamic-grids/column-definition";
import {DtoPriceChange} from "./models/DtoPriceChange";
import {PriceChangeCorrectionComponent} from "./price-change-correction.component";
import {PriceChangePercentageComponent} from "./price-change-percentage.component";
import {ChosenPercentage} from "./models/ChosenPercentage";
import {ReportPreviewComponent} from "../../shared/report-preview.component";

@Component({
    selector: 'treatment-price-change',
    template: require('./treatment-price-change.component.html'),
    styles: [require('./treatment-price-change.component.css')]
})
@AutoUnsubscribe()

export class TreatmentPriceChangeComponent implements OnInit {
    @Input() dialog: DialogRef;
    @ViewChild('grid', {static: false}) grid: DynamicGridComponent;
    pageSubheaderBarConfig: PageSubheaderBarConfig;
    formSettings: FormGroup;
    columnDefinition: ColumnDefinition[] = [
        {
            key: 'index',
            label: 'Nummer',
            type: 'number',
            width: '110px'
        },
        {
            key: 'description',
            label: 'Omschrijving',
            type: 'string',
            width: '*'
        },
        {
            key: 'oldPrice',
            label: 'Huidige prijs',
            type: 'number',
            width: '120px',
            format: '{0:c}'
        },
        {
            key: 'newPrice',
            label: 'Nieuwe prijs',
            type: 'number',
            width: '120px',
            format: '{0:c}',
            enabled: true
        }
    ];
    dtoObject: DtoPriceChange;
    loadingExisitingObject: boolean;

    constructor(private fb: FormBuilder,
                private fs: FormService,
                private ds: SettingsCashregService,
                private ss: SharedService,
                private dialogService: DialogService) { }

    ngOnInit() {
        this.pageSubheaderBarConfig = {
            title: 'BEHANDELINGEN',
            buttonDefinition: [
                {
                    clickEventType: 'print',
                    description: 'PROEFAFDRUK',
                    isSmall: false
                },
                {
                    clickEventType: 'procentuel',
                    description: 'PROCENTUEEL AANPASSEN',
                    isSmall: false
                },
                {
                    clickEventType: 'future',
                    description: 'TOEKOMSTIGE WIJZIGINGEN',
                    isSmall: false
                }
            ]
        };

        this.formSettings = this.fb.group({
            changeDate: this.ss.getCurrentDate()
        });

        this.dtoObject = {
            isDone: false,
            kind: 1,
            id: 0,
            changeDate: this.ss.getCurrentDate(),
            priceChangeLines: []
        };

        this.generatePriceChangeLines();
    }

    generatePriceChangeLines() {
        this.ds.generateTreatmentPriceChangeLines().subscribe(response => {
            this.dtoObject.priceChangeLines = response;
            this.filterPriceChangeLines(false);
        });
    }

    filterPriceChangeLines(getChanges: boolean) {
        if (getChanges) {
            this.getGridPrices();
        }

        this.grid.showData(this.dtoObject.priceChangeLines);
    }

    btnSubHeaderBarClicked(event) {
        if (event == 'procentuel') {
            this.showProcentuelWindow();
        } else if (event == 'future') {
            this.showCorrectionWindow();
        } else if (event == 'print') {
            this.printPriceChanges();
        }
    }

    printPriceChanges() {
        let result = this.fs.patchFormToDto(this.dtoObject, this.formSettings);
        this.getGridPrices();

        this.ds.saveTreatmentTempPriceChanges(result).subscribe(res => {
            if (res.hasError) {
                this.ss.message(res.errorMessage);
            } else {
                const dialogRef = this.dialogService.open({
                    content: ReportPreviewComponent
                });

                const dialogContent: ReportPreviewComponent = dialogRef.content.instance;
                dialogContent.dialog = dialogRef;
                dialogContent.reportName = 'PriceChangeList';
                dialogContent.reportParams = [
                    {
                        name: 'Type',
                        value: 'BEHANDELINGEN'
                    }
                ];
            }
        });
    }

    showCorrectionWindow() {
        const dialogRef = this.dialogService.open({
            content: PriceChangeCorrectionComponent
        });

        const dialogContent: PriceChangeCorrectionComponent = dialogRef.content.instance;
        dialogContent.dialog = dialogRef;
        dialogContent.type = 'treatment';
        dialogContent.chosenId
            .subscribe((res: number) => {
                this.ds.getTreatmentPriceChangeById(res).subscribe(response => {
                    this.dtoObject = response;
                    this.loadingExisitingObject = true;
                    this.fs.patchDtoToForm(this.dtoObject, this.formSettings);
                    this.loadingExisitingObject = false;
                    this.filterPriceChangeLines(false);
                });
            });
        dialogContent.deletedId
            .subscribe((res: number) => {
                if (this.dtoObject.id == res)
                    this.closeWindow(false);
            });
    }

    showProcentuelWindow() {
        const dialogRef = this.dialogService.open({
            content: PriceChangePercentageComponent
        });

        const dialogContent: PriceChangePercentageComponent = dialogRef.content.instance;
        dialogContent.dialog = dialogRef;
        dialogContent.chosenPercentage
            .subscribe((res: ChosenPercentage) => {
                this.dtoObject.priceChangeLines.forEach(item => {
                    item.newPrice = this.ss.roundToCents((item.oldPrice * (100 + res.percentage) / 100), res.round);
                    item.isPercentage = true;
                });
                this.filterPriceChangeLines(false);
            });
    }

    btnCancelClicked(event) {
        this.closeWindow(false);
    }

    btnSaveClicked(event) {
        this.ss.confirm('Weet u zeker dat u de prijswijzigingen wilt doorvoeren?')
            .subscribe((res: boolean) => {
                if (res) {
                    if (this.dtoObject.id > 0) {
                        this.savePriceChanges();
                    } else {
                        this.ds.treatmentHasFuturePriceChange().subscribe(res => {
                            if (res) {
                                this.ss.message('U kunt geen prijzen wijzigen omdat u al een prijswijziging in de toekomst klaar heeft staan. ' +
                                    'Wilt u nu toch prijzen wijzigen? Klik dan op Toekomstige Prijswijziging en verwijder de geagendeerde prijswijziging.');
                            } else {
                                this.savePriceChanges();
                            }
                        });
                    }
                }
            });
    }

    savePriceChanges() {
        let result = this.fs.patchFormToDto(this.dtoObject, this.formSettings);
        this.getGridPrices();
        this.ds.saveTreatmentPriceChanges(result).subscribe(res => {
            if (res.hasError) {
                this.ss.message(res.errorMessage);
            } else {
                this.closeWindow(result.changeDate == this.ss.getCurrentDate());
            }
        });
    }

    getGridPrices() {
        if (this.grid.getData()) {
            this.grid.getData().forEach(item => {
                if (this.dtoObject.priceChangeLines.find(x => x.treatmentId == item.treatmentId))
                    this.dtoObject.priceChangeLines.find(x => x.treatmentId == item.treatmentId).newPrice = item.newPrice;
            });
        }
    }

    closeWindow(result: boolean) {
        if (this.dialog) {
            this.dialog.dialog.instance.action.emit(result);
            this.dialog.close();
        }
    }
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./Scripts/app/settings/settings-cashreg/treatment-price-change.component.ts